import { notificationsClient } from "@core/clients";
import { QueryFunctionContext, useInfiniteQuery, UseInfiniteQueryOptions } from "@tanstack/react-query";
import { NotificationQueryParams, NotificationReadRecord, NotificationWithReadStatus } from "@core/types";
import { NOTIFICATIONS_CACHE_EXPIRE_TIME_MS, NOTIFICATIONS_STALE_TIME_MS } from "../../config/constansts";

const transformWithReadStatus = (
  notifications: any[],
  readRecords: NotificationReadRecord[],
  isPublicOnly: boolean = false,
): NotificationWithReadStatus[] => {
  if (isPublicOnly) {
    return notifications.map((notification) => ({
      ...notification,
      isRead: false,
      readAt: null,
    }));
  }

  const readMap = new Map<number, string>();
  readRecords.forEach((read) => {
    readMap.set(read.notification_id, read.created_at);
  });

  return notifications.map((notification) => ({
    ...notification,
    isRead: readMap.has(notification.id),
    readAt: readMap.get(notification.id) || null,
  }));
};

type QueryParams = NotificationQueryParams;
type QueryKey = ["notifications", QueryParams];
type Options = UseInfiniteQueryOptions<
  NotificationWithReadStatus[],
  any,
  NotificationWithReadStatus[],
  NotificationWithReadStatus[],
  QueryKey
>;

const useInfiniteGetNotificationsQuery = (config: { options?: Options; params?: QueryParams } = {}) => {
  return useInfiniteQuery<NotificationWithReadStatus[], any, NotificationWithReadStatus[], QueryKey>({
    queryKey: ["notifications", config.params!],
    queryFn: async (queryContext: QueryFunctionContext<QueryKey, QueryParams>) => {
      const {
        pageParam = {
          page: config.params?.page || 1,
          perPage: config.params?.perPage || 20,
          customerId: config.params?.customerId,
          type: config.params?.type,
          search: config.params?.search,
          isPublicOnly: config.params?.isPublicOnly || false,
        },
        queryKey,
      } = queryContext;
      const configParams = queryKey[1];

      const notifications = await notificationsClient.getNotifications({
        isPublicOnly: pageParam.isPublicOnly,
        page: pageParam.page,
        perPage: pageParam.perPage,
        search: pageParam.search,
        type: pageParam.type || undefined,
      });

      if (!notifications || notifications.length === 0) {
        return [];
      }

      let readRecords: NotificationReadRecord[] = [];

      if (!pageParam.isPublicOnly && configParams?.customerId) {
        const notificationIds = notifications.map((n) => n.id);
        readRecords = await notificationsClient.fetchReadRecords({
          notificationIds,
        });
      }

      return transformWithReadStatus(notifications, readRecords, pageParam.isPublicOnly);
    },
    getNextPageParam: (lastRes, allData) => {
      const perPage = config.params?.perPage || 20;
      const itemsAmount = allData.reduce((a: number, b: NotificationWithReadStatus[]) => a + b.length, 0);

      if (lastRes.length === perPage) {
        return {
          ...config.params,
          page: Math.ceil(itemsAmount / perPage) + 1,
        };
      }

      return undefined;
    },
    cacheTime: NOTIFICATIONS_CACHE_EXPIRE_TIME_MS,
    staleTime: NOTIFICATIONS_STALE_TIME_MS,
    ...config.options,
  });
};

export default useInfiniteGetNotificationsQuery;
